import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  BlockQuote,
  Bold,
  ClassicEditor,
  Essentials,
  Heading,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  Italic,
  Link,
  List,
  MediaEmbed,
  Paragraph,
  Table,
  TableToolbar,
  Undo,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { useMemo } from 'react';
import { testApi } from '../lib/testApi';

type TestRichTextEditorProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  minHeight?: number;
};

class LocalUploadAdapter {
  private loader: { file: Promise<File> };

  constructor(loader: { file: Promise<File> }) {
    this.loader = loader;
  }

  upload() {
    return this.loader.file.then((file) => testApi.uploadFile(file).then(({ imageUrl }) => ({ default: imageUrl })));
  }

  abort() {}
}

function localUploadAdapterPlugin(editor: any) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader: { file: Promise<File> }) => new LocalUploadAdapter(loader);
}

const editorConfig = {
  licenseKey: 'GPL',
  plugins: [
    Essentials,
    Paragraph,
    Heading,
    Bold,
    Italic,
    BlockQuote,
    List,
    Indent,
    Link,
    Image,
    ImageCaption,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    MediaEmbed,
    Table,
    TableToolbar,
    Undo,
  ],
  extraPlugins: [localUploadAdapterPlugin],
  toolbar: {
    items: [
      'undo', 'redo', '|', 'bold', 'italic', 'blockQuote', '|',
      'numberedList', 'bulletedList', 'outdent', 'indent', '|',
      'insertImage', 'mediaEmbed', 'link', '|', 'heading', 'insertTable',
    ],
  },
  image: {
    toolbar: ['imageTextAlternative', 'imageStyle:inline', 'imageStyle:block', 'imageStyle:side', 'resizeImage'],
  },
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
  },
};

export function TestRichTextEditor({ label, value, onChange, required = false, placeholder, minHeight = 170 }: TestRichTextEditorProps) {
  const config = useMemo(() => ({ ...editorConfig, placeholder }), [placeholder]);
  return <div className="test-rich-editor" style={{ '--test-editor-min-height': `${minHeight}px` } as React.CSSProperties}>
    <label><span>{label}{required && <b>*</b>}</span></label>
    <CKEditor editor={ClassicEditor} config={config} data={value} disabled={false} onChange={(_, editor) => onChange(editor.getData())} />
  </div>;
}
